import React, { useState, useEffect, useMemo } from 'react';
import { toast } from '../Toast';
import {
  FolderHeart, Save, Plus, Trash2, Users, Wallet, Calendar, Target,
  FileText, ExternalLink, Loader2, CheckCircle2, Lightbulb, Handshake,
  Clock, FolderOpen, Link2
} from 'lucide-react';
import { crmApi } from '../../services/crmApi';

const GRANT_PROGRAMS = [
  { value: 'proo_1a', label: 'PROO 1a' }, { value: 'proo_1b', label: 'PROO 1b' },
  { value: 'proo_2', label: 'PROO 2' }, { value: 'nowefio', label: 'NOWEFIO' },
  { value: 'ferc', label: 'FERC' }, { value: 'fe_malopolska', label: 'FE Małopolska' },
  { value: 'erasmus', label: 'Erasmus+' }, { value: 'cerv', label: 'CERV' },
  { value: 'kreator_kultury', label: 'Kreator Kultury' }, { value: 'nck', label: 'NCK' },
  { value: 'mhp', label: 'MHP' }, { value: 'bgk_pes', label: 'BGK PES' },
  { value: 'inne', label: 'Inny program' },
];

const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Niski', color: 'bg-gray-100 text-gray-700' },
  { value: 'medium', label: 'Średni', color: 'bg-blue-100 text-blue-700' },
  { value: 'high', label: 'Wysoki', color: 'bg-orange-100 text-orange-700' },
  { value: 'critical', label: 'Krytyczny', color: 'bg-red-100 text-red-700' },
];

interface BudgetRow { category: string; requested: number; own: number }
interface TeamMember { name: string; role: string }
interface Milestone { title: string; deadline: string; done: boolean }

const TABS = [
  { id: 'concept', label: 'Koncepcja', icon: Lightbulb, color: 'text-amber-600 bg-amber-50 border-amber-200' },
  { id: 'budget', label: 'Budżet', icon: Wallet, color: 'text-emerald-600 bg-emerald-50 border-emerald-200' },
  { id: 'team', label: 'Zespół & Partnerzy', icon: Handshake, color: 'text-indigo-600 bg-indigo-50 border-indigo-200' },
  { id: 'timeline', label: 'Harmonogram', icon: Clock, color: 'text-fuchsia-600 bg-fuchsia-50 border-fuchsia-200' },
  { id: 'docs', label: 'Dokumenty & Notatki', icon: FolderOpen, color: 'text-sky-600 bg-sky-50 border-sky-200' },
] as const;

type TabId = typeof TABS[number]['id'];

interface Props {
  application: any;
  onSaved: () => void;
}

export const GrantDetailSection: React.FC<Props> = ({ application, onSaved }) => {
  const app = application;
  const [activeTab, setActiveTab] = useState<TabId>('concept');
  const [saving, setSaving] = useState(false);
  const [dirtyTabs, setDirtyTabs] = useState<Set<TabId>>(new Set());

  // === All form state ===
  // Concept
  const [projectTitle, setProjectTitle] = useState('');
  const [programName, setProgramName] = useState('');
  const [projectDescription, setProjectDescription] = useState('');
  const [goals, setGoals] = useState('');
  const [deadline, setDeadline] = useState('');
  const [priority, setPriority] = useState('medium');

  // Budget
  const [requestedAmount, setRequestedAmount] = useState('');
  const [ownContribution, setOwnContribution] = useState('');
  const [budget, setBudget] = useState<BudgetRow[]>([]);

  // Team & Partners
  const [team, setTeam] = useState<TeamMember[]>([]);
  const [partners, setPartners] = useState('');

  // Timeline
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');
  const [milestones, setMilestones] = useState<Milestone[]>([]);

  // Docs & Notes
  const [programUrl, setProgramUrl] = useState('');
  const [gdriveUrl, setGdriveUrl] = useState('');
  const [notes, setNotes] = useState('');

  // Initialize from application data
  useEffect(() => {
    if (!app) return;
    setProjectTitle(app.grant_fpn_project_title || '');
    setProgramName(app.grant_fpn_program_name || '');
    setProjectDescription(app.grant_fpn_project_description || '');
    setGoals(app.grant_fpn_goals || '');
    setDeadline(app.grant_fpn_deadline?.split('T')[0] || '');
    setPriority(app.grant_fpn_priority || 'medium');
    setRequestedAmount(app.grant_fpn_requested_amount?.toString() || '');
    setOwnContribution(app.grant_fpn_own_contribution?.toString() || '');
    setBudget(Array.isArray(app.grant_fpn_budget) ? app.grant_fpn_budget : []);
    setTeam(Array.isArray(app.grant_fpn_team) ? app.grant_fpn_team : []);
    setPartners(app.grant_fpn_partners || '');
    setPeriodStart(app.grant_fpn_period_start?.split('T')[0] || '');
    setPeriodEnd(app.grant_fpn_period_end?.split('T')[0] || '');
    setMilestones(Array.isArray(app.grant_fpn_milestones) ? app.grant_fpn_milestones : []);
    setProgramUrl(app.grant_fpn_program_url || '');
    setGdriveUrl(app.grant_fpn_gdrive_url || '');
    setNotes(app.grant_fpn_notes || '');
    setDirtyTabs(new Set());
  }, [app?.id]);

  const markDirty = (tab: TabId) => setDirtyTabs(prev => new Set(prev).add(tab));

  // Tab data maps for saving
  const getTabData = (tab: TabId): Record<string, any> => {
    switch (tab) {
      case 'concept': return {
        grant_fpn_project_title: projectTitle || null,
        grant_fpn_program_name: programName || null,
        grant_fpn_project_description: projectDescription || null,
        grant_fpn_goals: goals || null,
        grant_fpn_deadline: deadline || null,
        grant_fpn_priority: priority,
      };
      case 'budget': return {
        grant_fpn_requested_amount: requestedAmount ? parseFloat(requestedAmount) : null,
        grant_fpn_own_contribution: ownContribution ? parseFloat(ownContribution) : null,
        grant_fpn_budget: budget.length > 0 ? budget : null,
      };
      case 'team': return {
        grant_fpn_team: team.length > 0 ? team : null,
        grant_fpn_partners: partners || null,
      };
      case 'timeline': return {
        grant_fpn_period_start: periodStart || null,
        grant_fpn_period_end: periodEnd || null,
        grant_fpn_milestones: milestones.length > 0 ? milestones : null,
      };
      case 'docs': return {
        grant_fpn_program_url: programUrl || null,
        grant_fpn_gdrive_url: gdriveUrl || null,
        grant_fpn_notes: notes || null,
      };
    }
  };

  const handleSaveTab = async (tab: TabId) => {
    setSaving(true);
    try {
      await crmApi.updateApplication(app.id, getTabData(tab) as any);
      setDirtyTabs(prev => { const n = new Set(prev); n.delete(tab); return n; });
      onSaved();
    } catch (err) {
      console.error('Grant save error:', err);
      toast.error('Błąd zapisu danych projektu');
    } finally {
      setSaving(false);
    }
  };

  // Tab fill check
  const tabFilled = useMemo(() => ({
    concept: !!(projectTitle || programName || projectDescription),
    budget: !!(requestedAmount || budget.length > 0),
    team: !!(team.length > 0 || partners),
    timeline: !!(periodStart || periodEnd || milestones.length > 0),
    docs: !!(programUrl || gdriveUrl || notes),
  }), [projectTitle, programName, projectDescription, requestedAmount, budget, team, partners, periodStart, periodEnd, milestones, programUrl, gdriveUrl, notes]);

  const filledCount = Object.values(tabFilled).filter(Boolean).length;

  // Budget helpers
  const addBudgetRow = () => { setBudget(prev => [...prev, { category: '', requested: 0, own: 0 }]); markDirty('budget'); };
  const removeBudgetRow = (i: number) => { setBudget(prev => prev.filter((_, idx) => idx !== i)); markDirty('budget'); };
  const updateBudgetRow = (i: number, field: keyof BudgetRow, value: string | number) => {
    setBudget(prev => prev.map((row, idx) => idx === i ? { ...row, [field]: value } : row));
    markDirty('budget');
  };
  const totalRequested = budget.reduce((sum, r) => sum + (Number(r.requested) || 0), 0);
  const totalOwn = budget.reduce((sum, r) => sum + (Number(r.own) || 0), 0);

  // Team helpers
  const addTeamMember = () => { setTeam(prev => [...prev, { name: '', role: '' }]); markDirty('team'); };
  const removeTeamMember = (i: number) => { setTeam(prev => prev.filter((_, idx) => idx !== i)); markDirty('team'); };
  const updateTeamMember = (i: number, field: keyof TeamMember, value: string) => {
    setTeam(prev => prev.map((m, idx) => idx === i ? { ...m, [field]: value } : m));
    markDirty('team');
  };

  // Milestone helpers
  const addMilestone = () => { setMilestones(prev => [...prev, { title: '', deadline: '', done: false }]); markDirty('timeline'); };
  const removeMilestone = (i: number) => { setMilestones(prev => prev.filter((_, idx) => idx !== i)); markDirty('timeline'); };
  const updateMilestone = (i: number, field: keyof Milestone, value: any) => {
    setMilestones(prev => prev.map((m, idx) => idx === i ? { ...m, [field]: value } : m));
    markDirty('timeline');
  };

  const inputCls = 'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500';
  const labelCls = 'block text-xs font-medium text-gray-600 mb-1';

  const isTabDirty = dirtyTabs.has(activeTab);

  return (
    <div className="bg-white rounded-lg shadow">
      {/* Header + progress */}
      <div className="px-6 pt-5 pb-3 border-b">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-bold flex items-center gap-2">
            <FolderHeart size={22} className="text-amber-600" />
            Workflow wniosku grantowego
          </h3>
          <div className="flex items-center gap-2 text-sm">
            <div className="flex gap-0.5">
              {[0,1,2,3,4].map(i => (
                <div key={i} className={`w-3 h-3 rounded-full ${i < filledCount ? 'bg-amber-500' : 'bg-gray-200'}`} />
              ))}
            </div>
            <span className="text-gray-500 text-xs">{filledCount}/5</span>
          </div>
        </div>

        {/* Tab bar */}
        <div className="flex gap-1 overflow-x-auto pb-1">
          {TABS.map(tab => {
            const Icon = tab.icon;
            const active = activeTab === tab.id;
            const filled = tabFilled[tab.id];
            const dirty = dirtyTabs.has(tab.id);
            return (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap transition-all border
                  ${active ? tab.color + ' shadow-sm' : 'bg-white text-gray-500 border-transparent hover:bg-gray-50'}
                `}>
                <Icon size={14} />
                {tab.label}
                {filled && !dirty && <CheckCircle2 size={12} className="text-green-500" />}
                {dirty && <span className="w-2 h-2 rounded-full bg-orange-400 inline-block" />}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab content */}
      <div className="p-6">
        {/* ===== TAB 1: Koncepcja ===== */}
        {activeTab === 'concept' && (
          <div className="space-y-4">
            <div>
              <label className={labelCls}>Tytuł projektu</label>
              <input type="text" value={projectTitle} onChange={e => { setProjectTitle(e.target.value); markDirty('concept'); }}
                className={inputCls} placeholder="Nazwa projektu..." />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Program grantowy</label>
                <select value={programName} onChange={e => { setProgramName(e.target.value); markDirty('concept'); }}
                  className={inputCls}>
                  <option value="">— wybierz program —</option>
                  {GRANT_PROGRAMS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Deadline naboru</label>
                <input type="date" value={deadline} onChange={e => { setDeadline(e.target.value); markDirty('concept'); }}
                  className={inputCls} />
              </div>
            </div>
            <div>
              <label className={labelCls}>Opis projektu</label>
              <textarea value={projectDescription} onChange={e => { setProjectDescription(e.target.value); markDirty('concept'); }}
                className={inputCls + ' min-h-[100px]'} placeholder="Co chcemy zrobic? Jaki problem rozwiązujemy?" rows={4} />
            </div>
            <div>
              <label className={labelCls}>Cele projektu</label>
              <textarea value={goals} onChange={e => { setGoals(e.target.value); markDirty('concept'); }}
                className={inputCls + ' min-h-[80px]'} placeholder="Cel główny i cele szczegolowe projektu..." rows={3} />
            </div>
            <div>
              <label className={labelCls}>Priorytet</label>
              <div className="flex gap-2">
                {PRIORITY_OPTIONS.map(p => (
                  <button key={p.value} onClick={() => { setPriority(p.value); markDirty('concept'); }}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                      priority === p.value ? p.color + ' ring-2 ring-offset-1 ring-amber-400' : 'bg-gray-50 text-gray-500 hover:bg-gray-100'
                    }`}>
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ===== TAB 2: Budżet ===== */}
        {activeTab === 'budget' && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Kwota wnioskowana (zl)</label>
                <input type="number" value={requestedAmount} onChange={e => { setRequestedAmount(e.target.value); markDirty('budget'); }}
                  className={inputCls} placeholder="0.00" min="0" step="0.01" />
              </div>
              <div>
                <label className={labelCls}>Wklad wlasny (zl)</label>
                <input type="number" value={ownContribution} onChange={e => { setOwnContribution(e.target.value); markDirty('budget'); }}
                  className={inputCls} placeholder="0.00" min="0" step="0.01" />
              </div>
            </div>

            {/* Budget summary */}
            {(requestedAmount || ownContribution) && (
              <div className="bg-emerald-50 rounded-lg p-3 flex items-center justify-between text-sm">
                <span className="text-emerald-700 font-medium">Koszt calkowity:</span>
                <span className="text-emerald-800 font-bold text-lg">
                  {((parseFloat(requestedAmount) || 0) + (parseFloat(ownContribution) || 0)).toLocaleString('pl-PL')} zl
                </span>
              </div>
            )}

            {/* Budget table */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-bold text-gray-700 flex items-center gap-2">
                  <Wallet size={16} className="text-emerald-600" />
                  Pozycje budzetowe
                </h4>
                <button onClick={addBudgetRow} className="flex items-center gap-1 text-xs text-emerald-600 hover:text-emerald-800 font-medium">
                  <Plus size={14} /> Dodaj pozycje
                </button>
              </div>
              {budget.length > 0 ? (
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Kategoria</th>
                        <th className="text-right px-3 py-2 text-xs font-medium text-gray-500 w-36">Kwota wnioskowana</th>
                        <th className="text-right px-3 py-2 text-xs font-medium text-gray-500 w-36">Wklad wlasny</th>
                        <th className="w-10"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {budget.map((row, i) => (
                        <tr key={i} className="border-t">
                          <td className="px-3 py-1.5">
                            <input type="text" value={row.category} onChange={e => updateBudgetRow(i, 'category', e.target.value)}
                              className="w-full px-2 py-1 border border-gray-200 rounded text-sm" placeholder="np. Wynagrodzenia" />
                          </td>
                          <td className="px-3 py-1.5">
                            <input type="number" value={row.requested || ''} onChange={e => updateBudgetRow(i, 'requested', parseFloat(e.target.value) || 0)}
                              className="w-full px-2 py-1 border border-gray-200 rounded text-sm text-right" min="0" step="0.01" />
                          </td>
                          <td className="px-3 py-1.5">
                            <input type="number" value={row.own || ''} onChange={e => updateBudgetRow(i, 'own', parseFloat(e.target.value) || 0)}
                              className="w-full px-2 py-1 border border-gray-200 rounded text-sm text-right" min="0" step="0.01" />
                          </td>
                          <td className="px-3 py-1.5 text-center">
                            <button onClick={() => removeBudgetRow(i)} className="text-red-400 hover:text-red-600">
                              <Trash2 size={14} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-gray-50 font-medium">
                      <tr className="border-t">
                        <td className="px-3 py-2 text-xs text-gray-600">RAZEM</td>
                        <td className="px-3 py-2 text-right text-xs">{totalRequested.toLocaleString('pl-PL')} zl</td>
                        <td className="px-3 py-2 text-right text-xs">{totalOwn.toLocaleString('pl-PL')} zl</td>
                        <td></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              ) : (
                <p className="text-xs text-gray-400 italic">Brak pozycji budzetowych. Kliknij „Dodaj pozycje" aby rozpoczac.</p>
              )}
            </div>
          </div>
        )}

        {/* ===== TAB 3: Zespół & Partnerzy ===== */}
        {activeTab === 'team' && (
          <div className="space-y-5">
            {/* Team */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-bold text-gray-700 flex items-center gap-2">
                  <Users size={16} className="text-indigo-600" />
                  Zespol projektówy
                </h4>
                <button onClick={addTeamMember} className="flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-800 font-medium">
                  <Plus size={14} /> Dodaj osobe
                </button>
              </div>
              {team.length > 0 ? (
                <div className="space-y-2">
                  {team.map((member, i) => (
                    <div key={i} className="flex gap-2 items-center">
                      <input type="text" value={member.name} onChange={e => updateTeamMember(i, 'name', e.target.value)}
                        className="flex-1 px-3 py-1.5 border border-gray-200 rounded-lg text-sm" placeholder="Imie i nazwisko" />
                      <input type="text" value={member.role} onChange={e => updateTeamMember(i, 'role', e.target.value)}
                        className="flex-1 px-3 py-1.5 border border-gray-200 rounded-lg text-sm" placeholder="Rola w projekcie" />
                      <button onClick={() => removeTeamMember(i)} className="text-red-400 hover:text-red-600 p-1">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-gray-400 italic">Brak czlonkow zespolu. Kliknij „Dodaj osobe".</p>
              )}
            </div>

            {/* Partners */}
            <div>
              <label className={labelCls}>
                <Handshake size={14} className="inline mr-1 text-indigo-500" />
                Partnerzy konsorcjum / wspolpracy
              </label>
              <textarea value={partners} onChange={e => { setPartners(e.target.value); markDirty('team'); }}
                className={inputCls + ' min-h-[100px]'} rows={4}
                placeholder="Wymien organizacje partnerskie, ich role i zakres wspolpracy..." />
            </div>
          </div>
        )}

        {/* ===== TAB 4: Harmonogram ===== */}
        {activeTab === 'timeline' && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Okres realizacji — start</label>
                <input type="date" value={periodStart} onChange={e => { setPeriodStart(e.target.value); markDirty('timeline'); }}
                  className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Okres realizacji — koniec</label>
                <input type="date" value={periodEnd} onChange={e => { setPeriodEnd(e.target.value); markDirty('timeline'); }}
                  className={inputCls} />
              </div>
            </div>

            {periodStart && periodEnd && (
              <div className="bg-fuchsia-50 rounded-lg p-3 text-sm text-fuchsia-700">
                Czas trwania: {Math.round((new Date(periodEnd).getTime() - new Date(periodStart).getTime()) / (1000*60*60*24*30))} miesiecy
              </div>
            )}

            {/* Milestones */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-bold text-gray-700 flex items-center gap-2">
                  <Target size={16} className="text-fuchsia-600" />
                  Kamienie milowe
                </h4>
                <button onClick={addMilestone} className="flex items-center gap-1 text-xs text-fuchsia-600 hover:text-fuchsia-800 font-medium">
                  <Plus size={14} /> Dodaj kamien milowy
                </button>
              </div>
              {milestones.length > 0 ? (
                <div className="space-y-2">
                  {milestones.map((m, i) => (
                    <div key={i} className="flex gap-2 items-center">
                      <button onClick={() => updateMilestone(i, 'done', !m.done)}
                        className={`p-1 rounded ${m.done ? 'text-green-500' : 'text-gray-300 hover:text-gray-500'}`}>
                        <CheckCircle2 size={18} />
                      </button>
                      <input type="text" value={m.title} onChange={e => updateMilestone(i, 'title', e.target.value)}
                        className={`flex-1 px-3 py-1.5 border border-gray-200 rounded-lg text-sm ${m.done ? 'line-through text-gray-400' : ''}`}
                        placeholder="Opis kamienia milowego" />
                      <input type="date" value={m.deadline} onChange={e => updateMilestone(i, 'deadline', e.target.value)}
                        className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm w-40" />
                      <button onClick={() => removeMilestone(i)} className="text-red-400 hover:text-red-600 p-1">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-gray-400 italic">Brak kamieni milowych. Kliknij „Dodaj kamien milowy".</p>
              )}
            </div>
          </div>
        )}

        {/* ===== TAB 5: Dokumenty & Notatki ===== */}
        {activeTab === 'docs' && (
          <div className="space-y-4">
            <div>
              <label className={labelCls}>
                <ExternalLink size={14} className="inline mr-1" />
                URL regulaminu naboru
              </label>
              <div className="flex gap-2">
                <input type="url" value={programUrl} onChange={e => { setProgramUrl(e.target.value); markDirty('docs'); }}
                  className={inputCls} placeholder="https://..." />
                {programUrl && (
                  <a href={programUrl} target="_blank" rel="noopener noreferrer"
                    className="flex items-center px-3 py-2 text-sky-600 hover:text-sky-800 bg-sky-50 rounded-lg">
                    <ExternalLink size={16} />
                  </a>
                )}
              </div>
            </div>
            <div>
              <label className={labelCls}>
                <Link2 size={14} className="inline mr-1" />
                Link Google Drive (dokumenty projektówe)
              </label>
              <div className="flex gap-2">
                <input type="url" value={gdriveUrl} onChange={e => { setGdriveUrl(e.target.value); markDirty('docs'); }}
                  className={inputCls} placeholder="https://drive.google.com/..." />
                {gdriveUrl && (
                  <a href={gdriveUrl} target="_blank" rel="noopener noreferrer"
                    className="flex items-center px-3 py-2 text-sky-600 hover:text-sky-800 bg-sky-50 rounded-lg">
                    <ExternalLink size={16} />
                  </a>
                )}
              </div>
            </div>
            <div>
              <label className={labelCls}>
                <FileText size={14} className="inline mr-1" />
                Notatki operatorskie
              </label>
              <textarea value={notes} onChange={e => { setNotes(e.target.value); markDirty('docs'); }}
                className={inputCls + ' min-h-[120px]'} rows={5}
                placeholder="Notatki wewnetrzne, ustalenia, uwagi dla zespolu..." />
            </div>
          </div>
        )}

        {/* Save button per tab */}
        {isTabDirty && (
          <div className="mt-6 pt-4 border-t flex justify-end">
            <button onClick={() => handleSaveTab(activeTab)} disabled={saving}
              className="flex items-center gap-2 px-5 py-2.5 bg-amber-600 text-white rounded-lg font-medium hover:bg-amber-700 disabled:opacity-50 transition-colors">
              {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
              Zapisz {TABS.find(t => t.id === activeTab)?.label}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
